import { HubApiError } from "./api-client.js";

export interface ErrorMessageOptions {
  fallback: string;
  payloadTooLarge?: string;
}

// Specific, actionable messages keyed by the server-provided `errorCode`
// on flat `/api/users/*` responses. Centralised here so the create-user
// dialog and any future identity-surface mutation pick up the same wording.
const SPECIFIC_ERROR_CODE_MESSAGES: Record<string, string> = {
  user_duplicate: "An account already exists for that email.",
  user_invalid_email: "That email address is not valid.",
  email_delivery_failed:
    "The account was created but the sign-in email could not be sent. Ask an administrator to check the email settings.",
};

export function userFacingErrorMessage(error: unknown, options: ErrorMessageOptions): string {
  if (!(error instanceof HubApiError)) return options.fallback;

  if (error.errorCode === "GATEWAY_OFFLINE") return "Couldn't connect to specialists.";
  if (error.errorCode === "NETWORK_ERROR") return "Couldn't connect to the workspace.";
  if (error.errorCode === "PAYLOAD_TOO_LARGE") {
    return options.payloadTooLarge ?? "That file is too large.";
  }
  if (error.errorCode === "VALIDATION_ERROR") {
    // A 400 validation message is written for the operator (e.g. the accent
    // contrast failure, or the gateway's "toolId … not in the catalog"), so
    // surface it verbatim instead of the field-less generic. Fall back only
    // when the server sent no message.
    const detail = error.message.trim();
    return detail.length > 0 ? detail : "Some fields need attention.";
  }
  if (error.errorCode && SPECIFIC_ERROR_CODE_MESSAGES[error.errorCode]) {
    return SPECIFIC_ERROR_CODE_MESSAGES[error.errorCode];
  }
  if (error.status === 401 || error.status === 403) {
    return "You do not have access to perform that action.";
  }
  if (error.status >= 500) return "Couldn't complete that request.";

  return options.fallback;
}
